// @flow
import React, { Component, createRef, type Element, type ElementType } from 'react';
import classnames from 'classnames';
import { get } from 'lodash';

import { ESCAPE } from 'Utils/keyCodes';

import { themes } from './DropDown.themes';

type State = {
  isExpanded: boolean,
};

type DropDownProps = {
  children: (handler: () => void, isExpanded: boolean) => ?Element<*>,
  className?: string,
  contentStyle?: *,
  hasCloseOnClick?: boolean,
  isExpanded?: boolean,
  renderTrigger?: (handler: () => void, isExpanded: boolean) => Element<*>,
  rootRef?: { current: ?React$ElementRef<ElementType> },
  theme?: string,
  onClose?: () => void,
};

export class DropDown extends Component<DropDownProps, State> {
  static defaultProps = {
    isExpanded: false,
    theme: 'default',
  };

  rootRef: { current: HTMLDivElement | null } = createRef();
  contentRef: { current: HTMLDivElement | null } = createRef();

  constructor(props: DropDownProps) {
    super(props);
    this.state = {
      isExpanded: props.isExpanded || false,
    };
  }

  componentDidMount() {
    if (this.state.isExpanded) {
      this.addEventListeners();
    }
  }

  componentWillUnmount() {
    if (this.state.isExpanded) {
      this.removeEventListeners();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: DropDownProps) {
    if (nextProps.isExpanded !== this.props.isExpanded) {
      this.setState({ isExpanded: nextProps.isExpanded }, this.toggleEventListeners);
    }
  }

  addEventListeners() {
    if (typeof window !== 'undefined') {
      document.addEventListener('mouseup', this.onClickOutside, false);
      document.addEventListener('touchstart', this.onClickOutside, false);
      document.addEventListener('keydown', this.onKeyDown, false);
    }
  }

  removeEventListeners() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('mouseup', this.onClickOutside, false);
      document.removeEventListener('touchstart', this.onClickOutside, false);
      document.removeEventListener('keydown', this.onKeyDown, false);
    }
  }

  toggleEventListeners() {
    if (this.state.isExpanded) {
      this.addEventListeners();
    } else {
      this.removeEventListeners();
    }
  }

  expand = () => {
    this.setState({ isExpanded: true }, this.toggleEventListeners);
  };

  collapse = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }

    this.setState({ isExpanded: false }, this.toggleEventListeners);
  };

  onKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === ESCAPE) {
      this.collapse();
    }
  };

  onClickOutside = (e: MouseEvent | TouchEvent) => {
    if (
      !this.contentRef ||
      // $FlowFixMe
      this.contentRef.current.contains(e.target) ||
      // $FlowFixMe
      this.rootRef.current.contains(e.target) ||
      // $FlowFixMe
      (get(this.props, 'rootRef.current') && this.props.rootRef.current.contains(e.target)) ||
      (e.button && e.button !== 0)
    ) {
      return;
    }
    this.collapse();
  };

  toggle = () => {
    this.setState(
      prevState => ({
        isExpanded: !prevState.isExpanded,
      }),
      this.toggleEventListeners,
    );
  };

  render() {
    const { children, className, contentStyle, hasCloseOnClick, renderTrigger, theme } = this.props;

    const { thBody } = themes[theme];

    return (
      <div className={classnames(className, 'inline-block relative')} ref={this.rootRef}>
        {renderTrigger && renderTrigger(this.toggle, this.state.isExpanded)}
        {this.state.isExpanded && (
          <div
            className={classnames(
              'absolute left-1/2 min-w-full w-full -translate-x-50 z-4',
              thBody,
            )}
            data-testid="DropDown-body"
            onClick={hasCloseOnClick ? this.collapse : null}
            ref={this.contentRef}
            style={contentStyle}
          >
            {children(this.toggle, this.state.isExpanded)}
          </div>
        )}
      </div>
    );
  }
}
